/**
 * The demo page: a white screen, a sentence, and a conversation.
 *
 * A juror should not have to navigate anything to understand what Recall is or
 * what it did. They ask, and the answer arrives with its numbers attached.
 *
 * The important honesty property: nothing is generated when a question is
 * asked. Every answer is composed at build time from the committed artifacts
 * of one completed run, and a question the artifacts cannot support is refused
 * rather than improvised. That is the same rule the product itself follows,
 * demonstrated by the page that describes it.
 */

import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';

import './demo.css';

import { ANSWERS, byId, match } from './answers';
import type { AnswerGroup } from './answers';

interface Turn {
  id: number;
  from: 'you' | 'recall';
  content: ReactNode;
  more?: { href: string; label: string };
}

/**
 * The whole map, in reading order. A juror who scans rather than types should
 * be able to see every question the demo can answer without doing anything.
 */
const GROUP_ORDER: AnswerGroup[] = ['Start here', 'How it works', 'The proof', 'The limits'];
const GROUPED = GROUP_ORDER.map((group) => ({
  group,
  answers: ANSWERS.filter((answer) => answer.group === group),
})).filter((entry) => entry.answers.length > 0);

export function DemoPage() {
  const [turns, setTurns] = useState<Turn[]>([]);
  const [draft, setDraft] = useState('');
  const [asked, setAsked] = useState<Set<string>>(new Set());
  const threadRef = useRef<HTMLDivElement | null>(null);
  const nextId = useRef(0);

  /**
   * Bring the question that was just asked to the top of the view, so its
   * answer starts where the reader is looking. Scrolling to the end of the
   * thread would land them in the middle of a long answer.
   */
  useEffect(() => {
    if (turns.length === 0) {
      return;
    }
    // ':last-of-type' matches by element type, not by class, so the last
    // question is found by taking the last of all of them.
    const questions = threadRef.current?.querySelectorAll('.turn.you');
    const asked = questions?.[questions.length - 1];
    // Instant, and synchronous. A smooth scroll can be cancelled before it
    // arrives, and a scroll deferred to an animation frame never runs at all
    // while the tab is in the background; either way the reader would be left
    // in the middle of a long answer. The effect runs after the new turn is
    // in the DOM, so the position it reads is the final one.
    asked?.scrollIntoView({ behavior: 'auto', block: 'start' });
  }, [turns]);

  function ask(question: string, answerId?: string) {
    // A clicked suggestion knows exactly which answer it is; only typed text
    // goes through the matcher.
    const answer = answerId ? byId(answerId) : match(question);
    const id = nextId.current++;
    const reply: Turn = answer
      ? { id: id + 1000, from: 'recall', content: answer.body, more: answer.more }
      : {
          id: id + 1000,
          from: 'recall',
          content: (
            <>
              <p>
                I can only answer from this run’s stored artifacts, and I don’t have one that
                covers that. I would rather say so than improvise: which is the same rule the
                system itself follows.
              </p>
              <p className="quiet">Here is everything the artifacts do cover:</p>
              <p className="codes">
                {ANSWERS.slice(0, 8).map((option) => (
                  <button
                    key={option.id}
                    type="button"
                    className="inline-chip"
                    onClick={() => ask(option.label, option.id)}
                  >
                    {option.label}
                  </button>
                ))}
              </p>
            </>
          ),
        };
    if (answer) {
      setAsked((previous) => new Set(previous).add(answer.id));
    }
    setTurns((previous) => [
      ...previous,
      { id, from: 'you', content: question },
      reply,
    ]);
  }

  function submit(event: React.FormEvent) {
    event.preventDefault();
    const question = draft.trim();
    if (question.length === 0) {
      return;
    }
    setDraft('');
    ask(question);
  }


  return (
    <div className="demo">
      <div className="demo-wrap">
        <header className="demo-hero">
          {/* Four hairlines that draw themselves in, in the Google palette. The
              only motion on the page, and it happens once. */}
          <div className="rules" aria-hidden>
            <span className="rule blue" />
            <span className="rule red" />
            <span className="rule yellow" />
            <span className="rule green" />
          </div>
          <p className="event">
            Built for the All Things Agentic Hackathon, in the Fortified Enterprise Fleet
            category.
          </p>
          <h1>Welcome to the Recall demo.</h1>
          <p className="lede">
            Recall is a zero-trust institutional agent fleet that keeps watching closed genomic
            cases after the appointment ends, and never lets a model become the scientific
            authority.
          </p>
          <p className="lede">
            Here you can interrogate a real run: 456 cases, six hours, unattended, on Google
            Cloud. Ask anything below and the answer comes back with the numbers it stands on.
          </p>
        </header>

        <div className="thread" role="log" aria-live="polite" ref={threadRef}>
          {turns.map((turn) =>
            turn.from === 'you' ? (
              <div className="turn you" key={turn.id}>
                <span className="who">you</span>
                <div className="bubble">{turn.content}</div>
              </div>
            ) : (
              <div className="turn recall" key={turn.id}>
                <span className="who">recall</span>
                <div className="bubble">
                  {turn.content}
                  {turn.more ? (
                    <a className="more" href={turn.more.href}>
                      {turn.more.label} →
                    </a>
                  ) : null}
                </div>
              </div>
            ),
          )}
        </div>

        <div className="suggestions">
          {GROUPED.map((entry) => (
            <section className="chip-group" key={entry.group}>
              <span className="group-label">{entry.group}</span>
              <div className="chip-row">
                {entry.answers.map((answer) => (
                  <button
                    key={answer.id}
                    type="button"
                    className={asked.has(answer.id) ? 'chip asked' : 'chip'}
                    onClick={() => ask(answer.label, answer.id)}
                  >
                    {answer.label}
                  </button>
                ))}
              </div>
            </section>
          ))}
        </div>

        <form className="composer" onSubmit={submit}>
          <input
            type="text"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            placeholder="Ask about the run, the failures, the evidence, the limits…"
            aria-label="Ask about the run"
          />
          <button type="submit">Ask</button>
        </form>

        <p className="ground">
          Answers are composed from the committed artifacts of one completed execution
          (generation 27). Nothing is generated when you press Ask, and nothing is fetched while
          you read. A question the artifacts cannot support is refused rather than improvised.
        </p>

        <footer className="demo-foot">
          <a href="#/run">The run, case by case</a>
          <a href="#/story">The full story and architecture</a>
          <a href="#/demo/">The evidence surface</a>
          <span className="frame">
            NON-CLINICAL RESEARCH PROTOTYPE · SYNTHETIC RECORDS · CAPTURED PUBLIC EVIDENCE
          </span>
        </footer>
      </div>
    </div>
  );
}
